import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { phanCongAPI, lopHocAPI, monHocAPI, giaoVienAPI, tkbAPI } from '../services/api';
import { toast } from 'react-toastify';
import ConfirmDialog from '../components/ConfirmDialog';
import ImportExcel from '../components/ImportExcel';
import { FiUser, FiCheck, FiX, FiSearch, FiDownload, FiUpload, FiTrash2, FiFilter, FiBookOpen, FiAlertTriangle, FiUsers } from 'react-icons/fi';
import * as XLSX from 'xlsx';

const PhanCongPage = () => {
  const { t } = useTranslation();
  const [phanCongs, setPhanCongs] = useState([]);
  const [lopHocs, setLopHocs] = useState([]);
  const [monHocs, setMonHocs] = useState([]);
  const [giaoViens, setGiaoViens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGV, setSelectedGV] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cellToRemove, setCellToRemove] = useState(null);
  const [searchGV, setSearchGV] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [deleteAllPCOpen, setDeleteAllPCOpen] = useState(false);
  const [deleteAllTKBOpen, setDeleteAllTKBOpen] = useState(false);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    try {
      const [pcRes, lopRes, monRes, gvRes] = await Promise.all([
        phanCongAPI.getAll(),
        lopHocAPI.getAll(),
        monHocAPI.getAll(),
        giaoVienAPI.getAll()
      ]);

      if (pcRes.data.success) setPhanCongs(pcRes.data.data);
      if (lopRes.data.success) {
        // Sắp xếp lớp học theo ID tăng dần
        const sortedLop = [...lopRes.data.data].sort((a, b) => a.lop_id - b.lop_id);
        setLopHocs(sortedLop);
      }
      if (monRes.data.success) {
        // Sắp xếp môn học theo ID tăng dần
        const sortedMon = [...monRes.data.data].sort((a, b) => a.mon_id - b.mon_id);
        setMonHocs(sortedMon);
      }
      if (gvRes.data.success) {
        // Sắp xếp giáo viên theo ID tăng dần
        const sortedGV = [...gvRes.data.data].sort((a, b) => a.gv_id - b.gv_id);
        setGiaoViens(sortedGV);
      }
    } catch (error) {
      toast.error(t('phanCong.cannotLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    // Header row: [Lớp\Môn, tên các môn...]
    const header = ['Lớp \\ Môn', ...monHocs.map(m => m.ten_khoi ? `${m.ten_mon} (${m.ten_khoi})` : m.ten_mon)];
    const rows = lopHocs.map(lop => {
      const row = [lop.ten_lop];
      monHocs.forEach(mon => {
        const pc = phanCongs.find(p => p.lop_id === lop.lop_id && p.mon_id === mon.mon_id);
        if (pc) {
          const gv = giaoViens.find(g => g.gv_id === pc.gv_id);
          row.push(gv ? gv.ten_gv : '');
        } else {
          row.push('');
        }
      });
      return row;
    });
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Phân công');
    XLSX.writeFile(wb, 'phan_cong.xlsx');
  };

  const handleImport = async (file) => {
    const response = await phanCongAPI.import(file);
    if (response.data.success) {
      fetchAll();
    }
    return response;
  };

  const handleDeleteAllPC = async () => {
    try {
      const res = await phanCongAPI.deleteAll();
      toast.success(res.data.message);
      setDeleteAllPCOpen(false);
      fetchAll();
    } catch (error) {
      toast.error('Không thể xóa phân công.');
    }
  };

  const handleDeleteAllTKB = async () => {
    try {
      const res = await tkbAPI.deleteAll();
      toast.success(res.data.message);
      setDeleteAllTKBOpen(false);
    } catch (error) {
      toast.error('Không thể xóa TKB.');
    }
  };

  // Tìm phân công theo lớp và môn
  const findPhanCong = (lopId, monId) => {
    return phanCongs.find(pc => pc.lop_id === lopId && pc.mon_id === monId);
  };

  // Xử lý click vào ô trong bảng
  const handleCellClick = async (lopId, monId) => {
    const existingPC = findPhanCong(lopId, monId);

    if (existingPC) {
      // Nếu đã có phân công, hỏi xóa
      setCellToRemove({ lopId, monId, pc: existingPC });
      setConfirmOpen(true);
    } else if (selectedGV) {
      // Nếu chưa có và đã chọn GV, tạo phân công mới
      try {
        const res = await phanCongAPI.create({
          gvId: selectedGV.gv_id,
          monId: monId,
          lopId: lopId
        });
        // Cập nhật state trực tiếp từ response, không cần fetchAll
        if (res.data.success && res.data.data) {
          setPhanCongs(prev => [...prev, res.data.data]);
        } else {
          fetchAll();
        }
      } catch (error) {
        toast.error(error.response?.data?.message || t('phanCong.cannotCreate'));
      }
    } else {
      toast.warning(t('phanCong.selectTeacherFirst'));
    }
  };

  // Xác nhận xóa phân công
  const confirmRemove = async () => {
    if (!cellToRemove) return;

    try {
      await phanCongAPI.delete(cellToRemove.pc.pc_id);
      toast.success(t('phanCong.removeSuccess'));
      setConfirmOpen(false);
      // Xóa trực tiếp khỏi state, không cần fetchAll
      setPhanCongs(prev => prev.filter(pc => pc.pc_id !== cellToRemove.pc.pc_id));
      setCellToRemove(null);
    } catch (error) {
      toast.error(t('phanCong.cannotRemove'));
    }
  };

  // Lấy tên giáo viên từ phân công
  const getGVName = (pc) => {
    if (!pc) return null;
    const gv = giaoViens.find(g => g.gv_id === pc.gv_id);
    return gv ? gv.ten_gv : 'N/A';
  };

  // Lấy tên viết tắt của giáo viên
  const getGVShortName = (pc) => {
    if (!pc) return null;
    const gv = giaoViens.find(g => g.gv_id === pc.gv_id);
    if (!gv) return 'N/A';

    // Lấy tên viết tắt (2 từ cuối)
    const parts = gv.ten_gv.split(' ');
    if (parts.length >= 2) {
      return parts.slice(-2).join(' ');
    }
    return gv.ten_gv;
  };

  // Đếm tổng số tiết/tuần của mỗi giáo viên (memoized O(1) lookup)
  const tietByGV = useMemo(() => {
    const monMap = Object.fromEntries(monHocs.map(m => [m.mon_id, m.so_tiet_tuan || 0]));
    const map = {};
    phanCongs.forEach(pc => {
      map[pc.gv_id] = (map[pc.gv_id] || 0) + (monMap[pc.mon_id] || 0);
    });
    return map;
  }, [phanCongs, monHocs]);

  const countTietByGV = (gvId) => tietByGV[gvId] || 0;

  // Lọc danh sách giáo viên theo từ khóa tìm kiếm
  const subjectOptions = useMemo(() => {
    const subjects = giaoViens
      .map(gv => (gv.chuyen_mon || '').trim())
      .filter(Boolean);
    return [...new Set(subjects)].sort();
  }, [giaoViens]);

  const filteredGiaoViens = useMemo(() => {
    let filtered = giaoViens;
    if (subjectFilter) {
      const filterLower = subjectFilter.toLowerCase();
      filtered = filtered.filter(gv => (gv.chuyen_mon || '').toLowerCase() === filterLower);
    }
    const keyword = searchGV.toLowerCase().trim();
    if (keyword) {
      filtered = filtered.filter(gv =>
        gv.ten_gv.toLowerCase().includes(keyword) ||
        (gv.chuyen_mon && gv.chuyen_mon.toLowerCase().includes(keyword))
      );
    }
    return filtered;
  }, [giaoViens, searchGV, subjectFilter]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="phan-cong-page">
      <div className="phan-cong-container">

        {/* ── Cột trái: Danh sách giáo viên ── */}
        <div className="gv-list-panel">

          {/* Header */}
          <div className="pc-panel-header gv-panel-header">
            <div className="pc-panel-title">
              <FiUsers size={16} />
              <span>{t('phanCong.gvListTitle')}</span>
            </div>
            <span className="pc-count-badge">{filteredGiaoViens.length}/{giaoViens.length}</span>
          </div>

          {/* Filter chuyên môn */}
          <div className="gv-filter-row">
            <FiFilter size={13} className="filter-icon" />
            <select
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
            >
              <option value="">{t('phanCong.allSpecialties')}</option>
              {subjectOptions.map(subject => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
          </div>

          {/* Tìm kiếm */}
          <div className="gv-search">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder={t('phanCong.searchPlaceholder')}
              value={searchGV}
              onChange={(e) => setSearchGV(e.target.value)}
            />
            {searchGV && (
              <button className="clear-search" onClick={() => setSearchGV('')}>
                <FiX />
              </button>
            )}
          </div>

          {/* Danh sách GV */}
          <div className="gv-list">
            {filteredGiaoViens.length === 0 ? (
              <div className="pc-empty-text">
                <FiUser size={28} style={{ opacity: 0.3 }} />
                <span>{giaoViens.length === 0 ? t('phanCong.noTeachers') : t('phanCong.teacherNotFound')}</span>
              </div>
            ) : (
              filteredGiaoViens.map(gv => {
                const isSelected = selectedGV?.gv_id === gv.gv_id;
                const initials = gv.ten_gv.split(' ').slice(-2).map(w => w[0]).join('').toUpperCase();
                return (
                  <div
                    key={gv.gv_id}
                    className={`gv-item${isSelected ? ' selected' : ''}`}
                    onClick={() => setSelectedGV(isSelected ? null : gv)}
                  >
                    <div className={`gv-avatar${isSelected ? ' selected' : ''}`}>{initials}</div>
                    <div className="gv-info">
                      <div className="gv-name">{gv.ten_gv}</div>
                      {gv.chuyen_mon && <div className="gv-subject">{gv.chuyen_mon}</div>}
                    </div>
                    <div className="gv-right">
                      <span className="gv-count">{countTietByGV(gv.gv_id)}t</span>
                      {isSelected && <FiCheck className="gv-check" />}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer: GV đang chọn */}
          {selectedGV ? (
            <div className="selected-gv-bar">
              <FiCheck size={14} />
              <div className="selected-gv-bar-text">
                <strong>{selectedGV.ten_gv}</strong>
                <span>{t('phanCong.clickToAssign')}</span>
              </div>
              <button className="deselect-btn" onClick={() => setSelectedGV(null)} title="Bỏ chọn"><FiX size={13} /></button>
            </div>
          ) : (
            <div className="no-selection-bar">
              <FiUser size={13} />
              <span>Chọn giáo viên để phân công</span>
            </div>
          )}
        </div>

        {/* ── Cột phải: Bảng phân công ── */}
        <div className="assignment-panel">

          {/* Header */}
          <div className="pc-panel-header assignment-panel-header">
            <div className="pc-panel-title">
              <FiBookOpen size={16} />
              <span>{t('phanCong.assignmentTitle')}</span>
            </div>
            <div className="pc-action-btns">
              <button className="pc-btn pc-btn-ghost" onClick={handleExportExcel} title="Export Excel">
                <FiDownload size={13} /> Export
              </button>
              <button className="pc-btn pc-btn-ghost" onClick={() => setImportOpen(true)} title="Import Excel">
                <FiUpload size={13} /> Import
              </button>
              <button className="pc-btn pc-btn-danger" onClick={() => setDeleteAllPCOpen(true)} title="Xóa toàn bộ phân công">
                <FiTrash2 size={13} /> Xóa PC
              </button>
              <button className="pc-btn pc-btn-danger" onClick={() => setDeleteAllTKBOpen(true)} title="Xóa toàn bộ TKB">
                <FiTrash2 size={13} /> Xóa TKB
              </button>
            </div>
          </div>

          {lopHocs.length === 0 || monHocs.length === 0 ? (
            <div className="pc-empty-panel">
              <FiAlertTriangle size={36} style={{ color: '#f59e0b', marginBottom: 8 }} />
              <p>{t('phanCong.needData')}</p>
            </div>
          ) : (
            <div className="assignment-table-wrapper">
              <table className="assignment-table">
                <thead>
                  <tr>
                    <th className="sticky-col th-lop">Lớp \ Môn</th>
                    {monHocs.map(mon => (
                      <th key={mon.mon_id} title={mon.ten_khoi ? `${mon.ten_mon} — ${mon.ten_khoi}` : mon.ten_mon}>
                        <div className="th-mon-name">{mon.ten_mon}</div>
                        {mon.ten_khoi && <div className="th-mon-khoi">{mon.ten_khoi}</div>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lopHocs.map((lop, rowIdx) => (
                    <tr key={lop.lop_id} className={rowIdx % 2 === 0 ? 'row-even' : 'row-odd'}>
                      <td className="sticky-col td-lop">{lop.ten_lop}</td>
                      {monHocs.map(mon => {
                        const pc = findPhanCong(lop.lop_id, mon.mon_id);
                        const isAssigned = !!pc;
                        const isCurrentGV = pc && selectedGV && pc.gv_id === selectedGV.gv_id;
                        const canAssign = !isAssigned && !!selectedGV;
                        return (
                          <td
                            key={`${lop.lop_id}-${mon.mon_id}`}
                            className={`assignment-cell${isCurrentGV ? ' cell-current' : isAssigned ? ' cell-assigned' : canAssign ? ' cell-can' : ''}`}
                            onClick={() => handleCellClick(lop.lop_id, mon.mon_id)}
                            title={isAssigned ? `${getGVName(pc)} — click để xóa` : selectedGV ? `Phân công: ${selectedGV.ten_gv}` : 'Chọn giáo viên trước'}
                          >
                            {isCurrentGV ? (
                              <span className="cell-label cell-label-current"><FiCheck size={10} />{getGVShortName(pc)}</span>
                            ) : isAssigned ? (
                              <span className="cell-label cell-label-assigned">{getGVShortName(pc)}</span>
                            ) : canAssign ? (
                              <span className="cell-plus">＋</span>
                            ) : null}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Legend */}
          <div className="assignment-legend">
            <div className="legend-item">
              <span className="legend-dot dot-current"></span>
              <span>GV đang chọn</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot dot-assigned"></span>
              <span>GV khác dạy</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot dot-can"></span>
              <span>Có thể phân công</span>
            </div>
          </div>
        </div>
      </div>

      {/* Import Excel */}
      <ImportExcel
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={handleImport}
        title="Import Phân công từ Excel"
        templateColumns={['Tên lớp', 'Tên môn', 'Tên giáo viên']}
        templateData={[
          ['10A1', 'Toán', 'Nguyễn Văn A'],
          ['10A2', 'Văn', 'Trần Thị B']
        ]}
        templateFileName="mau_phan_cong.xlsx"
      />

      {/* Confirm xóa 1 phân công */}
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setCellToRemove(null);
        }}
        onConfirm={confirmRemove}
        title={t('phanCong.confirmDeleteTitle')}
        message={t('phanCong.confirmDeleteMsg', { name: getGVName(cellToRemove?.pc) })}
      />

      {/* Confirm xóa toàn bộ phân công */}
      <ConfirmDialog
        isOpen={deleteAllPCOpen}
        onClose={() => setDeleteAllPCOpen(false)}
        onConfirm={handleDeleteAllPC}
        title="Xóa toàn bộ phân công"
        message="Bạn có chắc muốn xóa TOÀN BỘ phân công? Hành động này không thể hoàn tác."
      />

      {/* Confirm xóa toàn bộ TKB */}
      <ConfirmDialog
        isOpen={deleteAllTKBOpen}
        onClose={() => setDeleteAllTKBOpen(false)}
        onConfirm={handleDeleteAllTKB}
        title="Xóa toàn bộ TKB"
        message="Bạn có chắc muốn xóa TOÀN BỘ thời khóa biểu? Vùng xếp sẽ được giữ lại. Hành động này không thể hoàn tác."
      />

      <style>{`
        /* ═══ Layout ═══ */
        .phan-cong-page { height: 100%; }
        .phan-cong-container {
          display: grid;
          grid-template-columns: 240px 1fr;
          gap: 12px;
          height: calc(100vh - 112px);
        }
        .gv-list-panel, .assignment-panel {
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.06);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          border: 1px solid #e5e7eb;
        }

        /* ═══ Panel headers ═══ */
        .pc-panel-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 14px;
          flex-shrink: 0;
          border-bottom: 1px solid rgba(255,255,255,0.12);
        }
        .gv-panel-header {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
          color: #fff;
        }
        .assignment-panel-header {
          background: linear-gradient(135deg, #1e1b4b 0%, #312e81 60%, #1e40af 100%);
          color: #fff;
        }
        .pc-panel-title {
          display: flex;
          align-items: center;
          gap: 7px;
          font-size: 14px;
          font-weight: 700;
          letter-spacing: 0.01em;
        }
        .pc-count-badge {
          font-size: 11px;
          background: rgba(255,255,255,0.2);
          color: #fff;
          border-radius: 20px;
          padding: 2px 8px;
          font-weight: 600;
        }

        /* ═══ Action buttons (header right) ═══ */
        .pc-action-btns { display: flex; gap: 5px; }
        .pc-btn {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 9px;
          height: 28px;
          border: none;
          border-radius: 6px;
          font-size: 11.5px;
          font-weight: 600;
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.15s;
          font-family: inherit;
        }
        .pc-btn-ghost {
          background: rgba(255,255,255,0.15);
          color: #fff;
          border: 1px solid rgba(255,255,255,0.25);
        }
        .pc-btn-ghost:hover { background: rgba(255,255,255,0.28); }
        .pc-btn-danger {
          background: rgba(239,68,68,0.18);
          color: #fca5a5;
          border: 1px solid rgba(239,68,68,0.3);
        }
        .pc-btn-danger:hover {
          background: linear-gradient(135deg,#ef4444,#dc2626);
          color: #fff;
          border-color: transparent;
        }

        /* ═══ GV Filter ═══ */
        .gv-filter-row {
          padding: 7px 12px;
          border-bottom: 1px solid #f0f0f0;
          display: flex;
          align-items: center;
          gap: 6px;
          background: #fafafa;
        }
        .filter-icon { color: #9ca3af; flex-shrink: 0; }
        .gv-filter-row select {
          flex: 1;
          padding: 5px 8px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 12.5px;
          background: #fff;
          color: #374151;
        }
        .gv-filter-row select:focus { outline: none; border-color: #7c3aed; }

        /* ═══ GV Search ═══ */
        .gv-search {
          padding: 7px 12px;
          border-bottom: 1px solid #f0f0f0;
          position: relative;
          display: flex;
          align-items: center;
          background: #fafafa;
        }
        .gv-search .search-icon { position: absolute; left: 22px; color: #9ca3af; font-size: 13px; }
        .gv-search input {
          width: 100%;
          padding: 6px 28px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 12.5px;
          transition: border-color 0.2s;
        }
        .gv-search input:focus { outline: none; border-color: #7c3aed; }
        .clear-search {
          position: absolute;
          right: 18px;
          background: none;
          border: none;
          cursor: pointer;
          color: #9ca3af;
          padding: 3px;
          display: flex;
          align-items: center;
        }
        .clear-search:hover { color: #374151; }

        /* ═══ GV List ═══ */
        .gv-list { flex: 1; overflow-y: auto; padding: 6px 8px; }
        .pc-empty-text {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 30px 16px;
          color: #9ca3af;
          font-size: 13px;
        }
        .gv-item {
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 7px 8px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.15s;
          border: 1.5px solid transparent;
          margin-bottom: 2px;
        }
        .gv-item:hover { background: #f5f3ff; border-color: #ddd6fe; }
        .gv-item.selected { background: #ede9fe; border-color: #7c3aed; }

        .gv-avatar {
          width: 32px; height: 32px;
          border-radius: 50%;
          background: linear-gradient(135deg, #c4b5fd, #7c3aed);
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .gv-avatar.selected { background: linear-gradient(135deg, #7c3aed, #5b21b6); }

        .gv-info { flex: 1; min-width: 0; }
        .gv-name { font-size: 13px; font-weight: 600; color: #1f2937; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .gv-subject { font-size: 11px; color: #6b7280; margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

        .gv-right { display: flex; flex-direction: column; align-items: flex-end; gap: 3px; flex-shrink: 0; }
        .gv-count {
          font-size: 10px; font-weight: 700; color: #059669;
          background: #d1fae5; border-radius: 10px; padding: 1px 6px;
        }
        .gv-check { color: #7c3aed; }

        /* ═══ GV Footer ═══ */
        .selected-gv-bar {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 9px 12px;
          background: linear-gradient(135deg, #ede9fe, #ddd6fe);
          border-top: 1px solid #c4b5fd;
          color: #5b21b6;
          font-size: 12px;
          flex-shrink: 0;
        }
        .selected-gv-bar-text { flex: 1; min-width: 0; }
        .selected-gv-bar-text strong { display: block; font-size: 12.5px; color: #4c1d95; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .selected-gv-bar-text span { font-size: 11px; color: #7c3aed; }
        .deselect-btn {
          background: rgba(124,58,237,0.15);
          border: none;
          border-radius: 50%;
          width: 22px; height: 22px;
          cursor: pointer;
          color: #7c3aed;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: background 0.15s;
        }
        .deselect-btn:hover { background: rgba(124,58,237,0.3); }
        .no-selection-bar {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 9px 12px;
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
          color: #9ca3af;
          font-size: 12px;
          flex-shrink: 0;
        }

        /* ═══ Assignment table ═══ */
        .pc-empty-panel {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #6b7280;
          font-size: 14px;
          gap: 4px;
        }
        .assignment-table-wrapper { flex: 1; overflow: auto; }
        .assignment-table { border-collapse: collapse; font-size: 11.5px; width: 100%; }

        .assignment-table th {
          background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
          font-weight: 600;
          position: sticky;
          top: 0;
          z-index: 3;
          border: 1px solid #e2e8f0;
          padding: 5px 4px;
          text-align: center;
          min-width: 62px;
          color: #374151;
        }
        .th-lop { min-width: 56px !important; text-align: left !important; z-index: 4 !important; padding-left: 10px !important; }
        .th-mon-name { font-size: 11.5px; font-weight: 600; color: #1e3a5f; line-height: 1.2; }
        .th-mon-khoi { font-size: 9.5px; color: #94a3b8; font-weight: 400; margin-top: 1px; }

        .assignment-table td { border: 1px solid #e9ecef; padding: 0; text-align: center; height: 26px; vertical-align: middle; }

        .sticky-col {
          position: sticky;
          left: 0;
          z-index: 2;
          background: #f8fafc;
          border-right: 2px solid #e2e8f0 !important;
        }
        .td-lop {
          font-size: 12px;
          font-weight: 700;
          color: #1e3a5f;
          text-align: left;
          padding: 0 10px;
          white-space: nowrap;
        }
        .row-even .td-lop { background: #f8fafc; }
        .row-odd .td-lop { background: #f3f4f6; }
        .row-odd td { background: #fafafa; }

        .assignment-cell { cursor: pointer; transition: background 0.12s; }
        .assignment-cell:hover { background: #dbeafe !important; }

        .cell-current { background: #d1fae5 !important; box-shadow: inset 0 0 0 2px #10b981; }
        .cell-assigned { background: #fef3c7 !important; }
        .cell-can { background: #f5f3ff !important; }
        .cell-assigned:hover { background: #fde68a !important; }
        .cell-can:hover { background: #ede9fe !important; }

        .cell-label {
          display: inline-flex;
          align-items: center;
          gap: 2px;
          font-size: 10px;
          font-weight: 600;
          line-height: 1;
          padding: 2px 4px;
          border-radius: 4px;
        }
        .cell-label-current { color: #065f46; background: #a7f3d0; }
        .cell-label-assigned { color: #92400e; background: #fde68a; }
        .cell-plus { font-size: 14px; color: #7c3aed; opacity: 0.5; line-height: 1; }

        /* ═══ Legend ═══ */
        .assignment-legend {
          display: flex;
          gap: 16px;
          padding: 8px 16px;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
          flex-shrink: 0;
        }
        .legend-item { display: flex; align-items: center; gap: 5px; font-size: 11.5px; color: #6b7280; }
        .legend-dot { width: 12px; height: 12px; border-radius: 3px; flex-shrink: 0; }
        .dot-current { background: #a7f3d0; border: 1.5px solid #10b981; }
        .dot-assigned { background: #fde68a; border: 1.5px solid #f59e0b; }
        .dot-can { background: #ede9fe; border: 1.5px solid #7c3aed; }

        @media (max-width: 768px) {
          .phan-cong-container { grid-template-columns: 1fr; height: auto; }
          .gv-list-panel { max-height: 300px; }
        }
      `}</style>
    </div>
  );
};

export default PhanCongPage;
